/**
 * „MEGFELELŐ A TÍPUS?" — a jelzés a bizonylat fejlécében (#93).
 *
 * Viktor, 2026-11-03 (`docs/tervezes/design/irany-ellentmondas/`, B): ha a
 * papír más típust mutat, mint amit az ügyfél választott, a munkalista
 * jelez, és a könyvelő két dolgot tehet:
 *
 * - **„Javítom: …"** — megnyitja a #92 „Megváltoztatom" űrlapját, a
 *   kiolvasott típussal előre kiválasztva. NEM ment: a `Mentem` a könyvelőé,
 *   és az `Esc` mindent visszavon.
 * - **„Marad így"** — a típus jó; a jelzés erre a bizonylatra nem jön vissza.
 *
 * FELÜLÍRÁS NINCS. A gép javasol, a döntés a könyvelőé (H11).
 *
 * AZ OK KI VAN MONDVA („A papíron a cég az eladó."), mert anélkül a könyvelő
 * nem tudja, mihez mérje a papírt — és egy indoklás nélküli jelzést hamar
 * megtanul átugrani.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { docTypeLabel } from '@/i18n/generated';
import { api } from '@/lib/api-client';
import type { WorklistItem } from './types';
import type { FieldCorrectionState } from './useFieldCorrection';

export function TypeHintNotice({
  item,
  correction,
  onDismissed,
}: {
  item: WorklistItem;
  correction: FieldCorrectionState;
  /** A „Marad" után a lista újratölt — a sor címkéje is lekerül. */
  onDismissed: () => void;
}) {
  const { t } = useTranslation();
  const [keeping, setKeeping] = useState(false);
  const [failed, setFailed] = useState(false);

  const hint = item.typeHint;
  if (hint === null) {
    return null;
  }

  const current = docTypeLabel(t, item.docType);
  const read = docTypeLabel(t, hint.docType);

  const keep = () => {
    setKeeping(true);
    setFailed(false);
    api(`/documents/${item.id}/type-hint/dismiss`, { method: 'POST' })
      .then(() => onDismissed())
      .catch(() => setFailed(true))
      .finally(() => setKeeping(false));
  };

  return (
    // `role="note"`, NEM `alert`: a jelzés a bizonylat megnyitásakor már ott
    // van, nem egy esemény következménye — a képernyőolvasó ne kiabálja be
    // minden sorváltásnál.
    <section className="type-hint" role="note" aria-label={t('worklist.typeHint.heading')}>
      <p className="type-hint__heading">{t('worklist.typeHint.heading')}</p>
      <p className="type-hint__body">
        {item.clientDocType === null
          ? t('worklist.typeHint.currentAndRead', { current, read })
          : t('worklist.typeHint.clientAndRead', { current, read })}{' '}
        {t(`worklist.typeHint.reason.${hint.reason}`)}
      </p>
      <p className="type-hint__actions">
        <button
          type="button"
          className="type-hint__correct"
          onClick={() => correction.start(hint.docType)}
          disabled={correction.loading || keeping}
        >
          {t('worklist.typeHint.correct', { type: read })}
        </button>
        <button type="button" onClick={keep} disabled={keeping}>
          {t('worklist.typeHint.keep')}
        </button>
      </p>
      {failed ? (
        <p className="type-hint__error" role="alert">
          {t('worklist.typeHint.keepFailed')}
        </p>
      ) : null}
    </section>
  );
}
